import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Film, Eye, Bookmark, Sparkles, Edit3, Trash2 } from "lucide-react";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

export default function ProjectsList() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/projects/mine");
      setProjects(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/api/projects/${id}`);
      toast.success(t("projects_list.toast_deleted"));
      load();
    } catch (e) {
      toast.error(t("projects_list.toast_delete_failed"));
    }
  };

  const publish = async (id) => {
    try {
      await api.post(`/api/projects/${id}/publish`);
      toast.success(t("projects_list.toast_published"));
      load();
    } catch (e) {
      toast.error(t("projects_list.toast_publish_failed"));
    }
  };

  return (
    <DashboardLayout
      title={t("projects_list.title")}
      subtitle={t("projects_list.subtitle")}
      actions={
        <Button onClick={() => nav("/dashboard/proyectos/nuevo")} data-testid="button-create-project" className="bg-petroleum text-white hover:bg-petroleum/90 rounded-full px-5">
          <Plus size={16} className="mr-2" /> {t("projects_list.new_project")}
        </Button>
      }
    >
      {loading ? (
        <div className="text-slate-500">{t("common.loading")}</div>
      ) : projects.length === 0 ? (
        <div className="kpi-card p-10 text-center">
          <Film size={28} className="text-gold mx-auto" />
          <h3 className="font-display text-2xl text-petroleum mt-3">{t("projects_list.empty_title")}</h3>
          <p className="text-slate-500 mt-1">{t("projects_list.empty_desc")}</p>
          <Button onClick={() => nav("/dashboard/proyectos/nuevo")} className="mt-6 bg-petroleum text-white hover:bg-petroleum/90 rounded-full px-6" data-testid="button-first-project">
            <Plus size={16} className="mr-2" /> {t("projects_list.create_project")}
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {projects.map((p) => (
            <div key={p.id} className="kpi-card p-5 flex flex-col md:flex-row gap-5 items-start" data-testid={`row-project-${p.id}`}>
              <img src={p.cover_url || "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=400"} alt="" className="w-full md:w-40 h-28 object-cover rounded-lg" />
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h3 className="font-display text-2xl text-petroleum">{p.title}</h3>
                  {p.published ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{t("projects_list.badge_published")}</Badge> : <Badge variant="secondary">{t("projects_list.badge_draft")}</Badge>}
                </div>
                <p className="text-sm text-slate-500 mt-1 line-clamp-2">{p.logline}</p>
                <div className="mt-2 flex items-center gap-4 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1"><Eye size={12} /> {t("projects_list.views", { count: p.views || 0 })}</span>
                  <span className="inline-flex items-center gap-1"><Bookmark size={12} /> {t("projects_list.saves", { count: p.saves || 0 })}</span>
                  {p.format && <span className="capitalize">{p.format}</span>}
                  {p.stage && <span className="capitalize">{p.stage}</span>}
                </div>
              </div>
              <div className="flex flex-col gap-2 md:items-end">
                <Link to={`/dashboard/proyectos/${p.id}`} data-testid={`link-edit-project-${p.id}`} className="text-sm text-petroleum hover:text-gold inline-flex items-center gap-1">
                  <Edit3 size={12} /> {t("projects_list.edit")}
                </Link>
                {p.published ? (
                  <Link to={`/proyecto/${p.id}`} data-testid={`link-view-project-${p.id}`} className="text-sm text-petroleum hover:text-gold inline-flex items-center gap-1">
                    <Eye size={12} /> {t("projects_list.view_public")}
                  </Link>
                ) : (
                  <button onClick={() => publish(p.id)} data-testid={`button-publish-${p.id}`} className="text-sm text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-1">
                    <Sparkles size={12} /> {t("projects_list.publish")}
                  </button>
                )}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`button-delete-${p.id}`} className="text-sm text-red-600 hover:text-red-700 inline-flex items-center gap-1">
                      <Trash2 size={12} /> {t("projects_list.delete")}
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("projects_list.confirm_delete_title")}</AlertDialogTitle>
                      <AlertDialogDescription>{t("projects_list.confirm_delete_desc")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(p.id)}>{t("projects_list.delete")}</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
